<template>
  <div class="program-settings-content">
    <!-- Program Details Section -->
    <div class="settings-section">
      <div class="section-header">
        <img src="@/assets/MyWorkBench/Workflow/program_detail.svg" alt="Program Details" class="section-icon" />
        <span class="section-title">Program Details</span>
      </div>

      <!-- Program Name -->
      <div class="mb-3">
        <Label label="Program Name" :required="true">
          <TextInput
            v-model="programData.name"
            placeholder="Enter program name"
            :disabled="hasAssignees"
            size="medium"
          />
        </Label>
        <div v-if="showErrors && !programData.name" class="error-message">Required fields cannot be empty.</div>
      </div>

      <!-- Program Description -->
      <div class="mb-3">
        <Label label="Program Description">
          <TextArea
            v-model="programData.description"
            placeholder="Describe the program in detail..."
            :max_length="1000"
            :rows="4"
            :disabled="hasAssignees"
          />
        </Label>
        <div v-if="showErrors && !programData.description?.trim()" class="error-message">Program description cannot be empty.</div>
      </div>

      <!-- Duration Type -->
      <div class="mb-3">
        <Label label="Duration Type" :required="true">
          <SingleSelector
            v-model="programData.duration_type"
            placeholder="Please Select"
            size="medium"
            :options="durationTypeOptions"
            :disabled="hasAssignees"
            @change="handleDurationTypeChange"
          />
        </Label>
        <div v-if="showErrors && !programData.duration_type" class="error-message">Required fields cannot be empty.</div>

        <!-- Fixed Duration: Start and End Date -->
        <div v-if="programData.duration_type === 'fixed'" class="fixed-duration-fields">
          <div class="duration-label">
            Duration <span class="required-text">(Required)</span>
          </div>
          <div class="date-fields-container">
            <div class="date-field">
              <el-date-picker
                v-model="programData.start_date"
                type="date"
                placeholder="Start Date"
                class="duration-date-picker"
                :disabled="hasAssignees"
              />
            </div>
            <div class="date-field">
              <el-date-picker
                v-model="programData.end_date"
                type="date"
                placeholder="End Date"
                class="duration-date-picker"
                :disabled="hasAssignees"
              />
            </div>
          </div>
          <div v-if="showErrors && (!programData.start_date || !programData.end_date)" class="error-message">Required fields cannot be empty.</div>
        </div>

        <!-- Rolling Duration: Duration Value and Unit -->
        <div v-if="programData.duration_type === 'rolling'" class="rolling-duration-fields">
          <div class="duration-label">
            Duration <span class="required-text">(Required)</span>
          </div>
          <div class="rolling-container">
            <div class="rolling-value-field">
              <TextInput
                v-model="programData.durationValue"
                :clearable="false"
                type="number"
                placeholder="0"
                :disabled="hasAssignees"
                size="medium"
              />
            </div>
            <div class="rolling-unit-field">
              <SingleSelector
                v-model="programData.durationUnit"
                size="medium"
                placeholder="Unit"
                :options="unitOptions"
                :disabled="hasAssignees"
              />
            </div>
          </div>
          <div v-if="showErrors && (programData.durationValue === '' || programData.durationValue === null || !programData.durationUnit)" class="error-message">Required fields cannot be empty.</div>
        </div>
      </div>

      <!-- Enrollment Control -->
      <div class="mb-6">
        <div class="enrollment-control-row">
          <div class="enrollment-label">Enrollment Control</div>
          <div class="enrollment-toggle-row">
            <div class="toggle-track" :class="{ active: isEnrollmentControlOn, disabled: hasAssignees }" @click="hasAssignees ? null : toggleEnrollment()">
              <div class="toggle-thumb"></div>
            </div>
            <span class="toggle-label">{{ isEnrollmentControlOn ? 'ON' : 'OFF' }}</span>
          </div>
        </div>

        <!-- Maximum Enrollment Limit -->
        <div v-if="isEnrollmentControlOn" class="min-enrollment-field">
          <div class="enrollment-field-label">
            <span>Limit Patient Number to</span>
            <span class="required-text">(Required)</span>
          </div>
          <TextInput
            v-model="programData.maximum_enrollment"
            :clearable="false"
            type="number"
            placeholder="0"
            :disabled="hasAssignees"
            size="medium"
          />
        </div>
      </div>
    </div>

    <!-- Billing Details Section -->
    <div class="settings-section billing-section">
      <div class="billing-header">
        <img src="@/assets/MyWorkBench/Workflow/program_pricing.svg" alt="Program Pricing" class="billing-icon" />
        <span class="section-title">Program Pricing</span>
      </div>
      <div class="billing-description">
        <p>Please set the price for this program.</p>
      </div>

      <!-- Program Price -->
      <div class="mb-3">
        <Label label="Program Price" :required="true">
          <div class="price-input-wrapper">
            <span class="currency-symbol">$</span>
            <TextInput
              v-model="programData.price"
              :clearable="false"
              type="number"
              placeholder="0.00"
              :disabled="hasAssignees"
              size="medium"
            />
          </div>
        </Label>
        <div v-if="showErrors && (programData.price === null || programData.price === undefined || programData.price < 0)" class="error-message">Required fields cannot be empty.</div>
      </div>

      <!-- Total Order Cost Information -->
      <div v-if="totalOrderCost > 0" class="mb-3 cost-info">
        The cost of all the billable items included in this program is <strong>${{ totalOrderCost.toFixed(2) }}</strong>. You can customize to set your own price.
      </div>

      <div class="billing-amount-row mb-3">
        <span class="billing-amount-label">Period Billing Amount:</span>
        <span class="billing-amount-value">${{ programData.price }}</span>
      </div>

      <!-- Save Button -->
      <div class="save-btn-row">
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          width_type="fill-whole"
          size="small"
          :disabled="!isFormValid || hasAssignees"
          @click="handleSave"
        >
          Save Settings
        </ResponsiveButton>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgramSettingsContent',
  props: {
    value: {
      type: Object,
      default: () => ({
        name: ''
      })
    },
    hasAssignees: {
      type: Boolean,
      default: false
    },
    totalOrderCost: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      programData: {
        name: this.value?.name || '',
        description: this.value?.description || '',
        duration_type: this.value?.duration_type || '',
        start_date: this.value?.start_date || '',
        end_date: this.value?.end_date || '',
        durationValue: this.value?.durationValue || '',
        durationUnit: this.value?.durationUnit || '',
        maximum_enrollment: this.value?.maximum_enrollment !== undefined ? this.value.maximum_enrollment : null,
        price: this.value?.price !== undefined ? this.value.price : 0
      },
      showErrors: false,
      durationTypeOptions: [
        { label: 'Fixed', value: 'fixed' },
        { label: 'Rolling', value: 'rolling' }
      ],
      unitOptions: [
        { label: 'Day', value: 'days' },
        { label: 'Week', value: 'weeks' },
        { label: 'Month', value: 'months' }
      ]
    };
  },
  computed: {
    isEnrollmentControlOn() {
      return this.programData.maximum_enrollment !== null && this.programData.maximum_enrollment !== undefined;
    },
    isFormValid() {
      const hasRequiredBasicFields =
        this.programData.name?.trim() &&
        this.programData.duration_type &&
        (this.programData.price !== null &&
         this.programData.price !== undefined &&
         this.programData.price >= 0);

      const hasValidDuration =
        this.programData.duration_type === 'fixed'
          ? this.programData.start_date && this.programData.end_date
          : this.programData.duration_type === 'rolling'
            ? this.programData.durationValue !== '' && this.programData.durationValue !== null && this.programData.durationUnit
            : true;

      return hasRequiredBasicFields && hasValidDuration;
    }
  },
  watch: {
    value: {
      handler(newValue) {
        console.log('ProgramSettingsContent - value prop changed:', newValue);
        if (newValue) {
          // Only update if the value has actually changed from parent
          // Preserve local changes by checking if we're just echoing back our own changes
          Object.keys(newValue).forEach(key => {
            if (this.programData[key] !== newValue[key]) {
              this.programData[key] = newValue[key];
            }
          });
          console.log('ProgramSettingsContent - programData updated:', this.programData);
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleDurationTypeChange() {
      // Clear duration-specific fields when type changes
      this.programData.start_date = '';
      this.programData.end_date = '';
      this.programData.durationValue = '';
      this.programData.durationUnit = '';
    },
    toggleEnrollment() {
      if (this.isEnrollmentControlOn) {
        // Turn OFF: set to null
        this.programData.maximum_enrollment = null;
      } else {
        // Turn ON: set to default value of 1
        this.programData.maximum_enrollment = 1;
      }
    },
    validateForm() {
      // Program name
      if (!this.programData.name?.trim()) {
        return false;
      }
      // Duration type
      if (!this.programData.duration_type) {
        return false;
      }

      // Duration-specific validation
      if (this.programData.duration_type === 'fixed') {
        if (!this.programData.start_date || !this.programData.end_date) {
          return false;
        }
      } else if (this.programData.duration_type === 'rolling') {
        if (this.programData.durationValue === '' ||
            this.programData.durationValue === null ||
            !this.programData.durationUnit) {
          return false;
        }
      }

      // Billing amount
      if (this.programData.price === null ||
          this.programData.price === undefined ||
          this.programData.price < 0) {
        return false;
      }

      return true;
    },
    handleSave() {
      if (!this.validateForm()) {
        this.showErrors = true;
        return;
      }

      this.$emit('save', { ...this.programData });
    }
  }
};
</script>

<style scoped>
.program-settings-content {
  display: flex;
  flex-direction: column;
}

.settings-section:first-child {
  display: flex;
  flex-direction: column;
  padding-bottom: 12px;
  margin-bottom: 4px;
  border-bottom: 1px dashed #e5e7eb;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
}

.section-icon {
  width: 20px;
  height: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 800;
  color: #333;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-6 {
  margin-bottom: 24px;
}

.error-message {
  font-size: 12px;
  color: #EF4444;
  margin-top: 4px;
}

.required-text {
  color: #D10000;
}

/* Fixed Duration Fields */
.fixed-duration-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}

.duration-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  margin-bottom: 6px;
  margin-top: 8px;
}

.date-fields-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.date-field {
  flex: 1;
  min-width: 0;
}

:deep(.duration-date-picker) {
  width: 100%;
}

:deep(.duration-date-picker .el-input__wrapper) {
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  box-shadow: none;
}

/* Rolling Duration Fields */
.rolling-duration-fields {
  display: flex;
  flex-direction: column;
  margin-top: 4px;
}

.rolling-container {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.rolling-value-field {
  flex: 1;
  min-width: 0;
}

.rolling-unit-field {
  flex: 1;
  min-width: 0;
}

/* Enrollment Control */
.enrollment-control-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.enrollment-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
}

.enrollment-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-track {
  width: 44px;
  height: 24px;
  background-color: #ccc;
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;
}

.toggle-track.active {
  background-color: #004879;
}

.toggle-track.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-thumb {
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-track.active .toggle-thumb {
  transform: translateX(20px);
}

.toggle-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #333;
}

.min-enrollment-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.enrollment-field-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  display: flex;
  gap: 4px;
}

/* Billing Section */
.billing-section {
  padding: 16px 0;
}

.billing-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.billing-icon {
  width: 20px;
  height: 20px;
}

.billing-description p {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 12px 0;
}

.price-input-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.currency-symbol {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  flex-shrink: 0;
}

.cost-info {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #4A4A4A;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 6px;
  padding: 10px 12px;
  line-height: 1.5;
}

.billing-amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.billing-amount-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #4A4A4A;
}

.billing-amount-value {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.save-btn-row {
  display: flex;
  justify-content: flex-end;
}
</style>
